import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, AlertCircle, ArrowLeft, Receipt,
  Calendar, FileText, Building2, Package,
  Eye, Ban, Search, CheckCircle2, Save, X,
  ListChecks, TrendingUp, DollarSign, FileSpreadsheet,
} from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter as AlertFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchClients } from '@/pages/parametrizacion/clients/Services/clients.services';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import {
  filterLiquidaciones,
  crearLiquidacion,
  previewPendientes,
  getLiquidacionById,
  updateMetadataLiquidacion,
  updatePrecioItem,
  confirmarLiquidacion,
  anularLiquidacion,
} from './services/liquidaciones.service';

import LiquidacionDetailList from './components/LiquidacionDetailList';
import { exportarLiquidacionExcel } from './components/liquidacionExcel';
import { formatCurrency, formatDate, formatDateTime, todayStr } from './components/format';

// ─────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'liquidaciones', label: 'Liquidaciones' },
  { id: 'entregas', label: 'Entregas del período' },
];

const estadoColor = {
  Borrador: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  Liquidada: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  Anulada: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
};

// ─────────────────────────────────────────────────────────────────────
// Página de gestión de un cliente
// ─────────────────────────────────────────────────────────────────────
const LiquidacionClientePage = () => {
  const navigate = useNavigate();
  const { clienteId } = useParams();
  const { toast } = useToast();

  const [cliente, setCliente] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [tab, setTab] = useState('entregas');
  const [detailId, setDetailId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!clienteId) return;
    (async () => {
      setLoadingCliente(true);
      try {
        const res = await fetchClients({ limit: 1000, offset: 1, query: {} });
        const found = (res?.data || []).find((c) => String(c._id) === String(clienteId));
        setCliente(found || null);
      } catch (e) {
        toast({ variant: 'destructive', title: 'Error', description: e.message });
      } finally {
        setLoadingCliente(false);
      }
    })();
  }, [clienteId, toast]);

  if (loadingCliente) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">Cliente no encontrado.</p>
            <Button onClick={() => navigate('/financiera/liquidacion-clientes')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a la lista
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-4 lg:p-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-border/50">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/financiera/liquidacion-clientes')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="bg-emerald-500/15 p-2.5 rounded-lg">
              <Receipt className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                {cliente.nombre}
                {cliente.nit && (
                  <span className="text-sm text-gray-500 font-normal">NIT {cliente.nit}</span>
                )}
              </CardTitle>
              <CardDescription className="mt-1">
                Gestión de liquidaciones del cliente
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <div className="border-b border-border/40 px-4 flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <CardContent className="pt-6">
          <AnimatePresence mode="wait">
            {tab === 'liquidaciones' && (
              <motion.div
                key="liq"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.18 }}
              >
                <LiquidacionesTab
                  clienteId={clienteId}
                  onOpenDetail={setDetailId}
                  refreshKey={refreshKey}
                />
              </motion.div>
            )}
            {tab === 'entregas' && (
              <motion.div
                key="ent"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.18 }}
              >
                <EntregasTab
                  clienteId={clienteId}
                  clienteNombre={cliente.nombre}
                  clienteNit={cliente.nit}
                  onLiquidacionCreada={() => {
                    setRefreshKey((k) => k + 1);
                    setTab('liquidaciones');
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Modal: detalle de una liquidación existente */}
      <LiquidacionDetailModal
        open={!!detailId}
        liquidacionId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => setRefreshKey((k) => k + 1)}
      />
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Tab: Liquidaciones del cliente
// ─────────────────────────────────────────────────────────────────────
const LiquidacionesTab = ({ clienteId, onOpenDetail, refreshKey }) => {
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEstado, setFilterEstado] = useState('');
  const [anularOpen, setAnularOpen] = useState(null); // liq a anular
  const [motivo, setMotivo] = useState('');
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const query = { 'cliente.id': clienteId };
      if (filterEstado) query.estado = filterEstado;
      const res = await filterLiquidaciones({ query, limit: 200, page: 1 });
      setData(res.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [clienteId, filterEstado, toast]);

  useEffect(() => {
    cargar();
  }, [cargar, refreshKey]);

  const resumen = useMemo(() => {
    let total = 0;
    let countLiquidada = 0;
    let countBorrador = 0;
    data.forEach((l) => {
      if (l.estado === 'Liquidada') {
        total += Number(l.total || 0);
        countLiquidada++;
      } else if (l.estado === 'Borrador') {
        countBorrador++;
      }
    });
    return { total, countLiquidada, countBorrador, count: data.length };
  }, [data]);

  const handleConfirmar = async (liq) => {
    setBusy(true);
    try {
      await confirmarLiquidacion(liq._id, {
        id: user.id, nombre: user.name || user.raw?.nombre || 'Usuario',
      });
      toast({ title: 'Liquidación confirmada' });
      cargar();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleAnular = async () => {
    if (!motivo.trim()) {
      toast({ variant: 'destructive', title: 'Indica el motivo' });
      return;
    }
    setBusy(true);
    try {
      await anularLiquidacion(anularOpen._id, motivo.trim(), {
        id: user.id, nombre: user.name || user.raw?.nombre || 'Usuario',
      });
      toast({ title: 'Liquidación anulada' });
      setAnularOpen(null);
      setMotivo('');
      cargar();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Total liquidaciones</p>
          <p className="text-xl font-bold text-gray-900">{resumen.count}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">En Borrador</p>
          <p className="text-xl font-bold text-amber-700">{resumen.countBorrador}</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Confirmadas</p>
          <p className="text-xl font-bold text-emerald-700">{resumen.countLiquidada}</p>
        </div>
        <div className="bg-violet-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Total facturado</p>
          <p className="text-base font-bold text-violet-700">{formatCurrency(resumen.total)}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500">Filtrar:</span>
        {[
          { v: '', l: 'Todas' },
          { v: 'Borrador', l: 'Borrador' },
          { v: 'Liquidada', l: 'Liquidada' },
          { v: 'Anulada', l: 'Anulada' },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setFilterEstado(opt.v)}
            className={`px-3 h-7 text-xs rounded font-medium transition-colors ${
              filterEstado === opt.v
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Sin liquidaciones para este cliente.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.map((l) => (
            <div
              key={l._id}
              className={`border-l-4 rounded-md bg-white border border-border/60 hover:shadow-md transition-all p-3 flex flex-wrap items-center gap-3`}
              style={{
                borderLeftColor:
                  l.estado === 'Liquidada' ? '#10b981'
                  : l.estado === 'Borrador' ? '#f59e0b'
                  : '#f43f5e',
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold">{l.consecutivo}</span>
                  <span className="font-medium text-sm truncate">{l.nombre}</span>
                  <Badge className={estadoColor[l.estado] || ''}>{l.estado}</Badge>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {formatDate(l.fecha_creacion)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="h-3 w-3" /> {l.entregas?.length || 0} entregas
                  </span>
                  {l.fecha_liquidacion && (
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Liquidada {formatDate(l.fecha_liquidacion)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-base font-bold text-emerald-700">{formatCurrency(l.total)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpenDetail(l._id)}>
                    <Eye className="h-3 w-3 mr-1" /> Ver
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => exportarLiquidacionExcel({ liquidacion: l })}
                    title="Descargar Excel con el detalle"
                  >
                    <FileSpreadsheet className="h-3 w-3" />
                  </Button>
                  {l.estado === 'Borrador' && (
                    <Button
                      size="sm"
                      onClick={() => handleConfirmar(l)}
                      disabled={busy}
                      className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Confirmar
                    </Button>
                  )}
                  {l.estado === 'Liquidada' && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 text-xs"
                      onClick={() => { setAnularOpen(l); setMotivo(''); }}
                    >
                      <Ban className="h-3 w-3 mr-1" /> Anular
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!anularOpen} onOpenChange={(o) => !o && setAnularOpen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Anular esta liquidación?</AlertDialogTitle>
            <AlertDialogDescription>
              Se liberarán las entregas para que puedan incluirse en otra liquidación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs text-gray-500">Motivo de anulación *</label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. Error en cantidades / precios mal aplicados"
            />
          </div>
          <AlertFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAnular}
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Anular y liberar
            </AlertDialogAction>
          </AlertFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Tab: Entregas del período
// Layout:
//   1. Filtros (fechas + buscador)
//   2. KARDEX SUPERIOR — prendas agrupadas de las entregas seleccionadas
//   3. Stats (seleccionadas, prendas, total)
//   4. Lista de entregas con checkboxes
//   5. Botón "Crear liquidación"
// ─────────────────────────────────────────────────────────────────────
const EntregasTab = ({ clienteId, clienteNombre, clienteNit, onLiquidacionCreada }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Filtros
  const [dateStart, setDateStart] = useState('2026-01-01');
  const [dateEnd, setDateEnd] = useState(todayStr);
  const [search, setSearch] = useState('');

  // Datos
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [preciosPorPrenda, setPreciosPorPrenda] = useState({}); // { [prendaId]: { nombre, precioUnitario, cantidadTotal } }
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const startISO = new Date(dateStart + 'T00:00:00').toISOString();
      const endISO = new Date(dateEnd + 'T23:59:59').toISOString();
      const res = await fetchEntregasClientes({
        query: {
          'cliente.id': clienteId,
          tipo: 'Entrega',
          fecha_creacion: { $gte: startISO, $lte: endISO },
        },
        limit: 1000,
        page: 1,
      });
      const txs = res.data || [];
      setEntregas(txs);
      // Limpia selección si las entregas cambiaron
      setSelectedIds(new Set());
      // Carga precios sugeridos desde preview (que ya incluye el recargo
      // por tipo de entrega según los porcentajes del cliente)
      try {
        const preview = await previewPendientes(clienteId, dateStart, dateEnd);
        const map = {};
        (preview.data?.entregas || []).forEach((e) => {
          (e.items || []).forEach((it) => {
            if (!map[it.prendaId]) {
              map[it.prendaId] = {
                nombre: it.nombre,
                precioBase: Number(it.precioBase ?? it.precioUnitario ?? 0),
                porcentaje: Number(it.porcentaje ?? 100),
                precioUnitario: Number(it.precioUnitario || 0),
                recargo: Number(it.recargo || 0),
              };
            }
          });
        });
        setPreciosPorPrenda(map);
      } catch {
        // Si falla el preview, no pasa nada — los precios quedan en 0
      }
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [clienteId, dateStart, dateEnd, toast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Entregas seleccionadas
  const entregasSeleccionadas = useMemo(
    () => entregas.filter((e) => selectedIds.has(e._id) && !e.liquidada),
    [entregas, selectedIds],
  );

  // KARDEX: prendas agregadas de las entregas seleccionadas
  // + matriz { [prendaId]: { [entregaId]: cantidad } } para la tabla transaccional
  // + desglose por tipo de entrega (ordinaria/dominical/extra) por prenda
  const { kardex, kardexCantidades, entregasParaKardex, subtotalesPorTipo } = useMemo(() => {
    // Ordenar entregas por fecha para que las columnas tengan sentido cronológico
    const entregasOrd = [...entregasSeleccionadas].sort(
      (a, b) =>
        new Date(a.fecha_entrega || a.fecha_creacion) -
        new Date(b.fecha_entrega || b.fecha_creacion),
    );
    const prendasMap = new Map();
    const cantMap = {};
    // Acumuladores por tipo de entrega
    const subtotalesPorTipo = {
      ordinaria: { cantidad: 0, subtotal: 0 },
      dominical: { cantidad: 0, subtotal: 0 },
      extra: { cantidad: 0, subtotal: 0 },
    };

    entregasOrd.forEach((e) => {
      const tipo = e.tipoEntrega || 'ordinaria';
      (e.items || []).forEach((it) => {
        const cant = it.cantidad_confirmada || 0;
        if (cant <= 0) return;
        if (!prendasMap.has(it.prendaId)) {
          prendasMap.set(it.prendaId, {
            prendaId: it.prendaId,
            nombre: it.nombre,
            cantidad: 0,
            cantidadPorEntrega: {},
            desglosePorTipo: {
              ordinaria: { cantidad: 0, subtotal: 0 },
              dominical: { cantidad: 0, subtotal: 0 },
              extra: { cantidad: 0, subtotal: 0 },
            },
          });
        }
        const p = prendasMap.get(it.prendaId);
        p.cantidad += cant;
        p.cantidadPorEntrega[e._id] = (p.cantidadPorEntrega[e._id] || 0) + cant;
        cantMap[it.prendaId] = cantMap[it.prendaId] || {};
        cantMap[it.prendaId][e._id] = (cantMap[it.prendaId][e._id] || 0) + cant;

        // Precio actual (puede ser custom)
        const info = preciosPorPrenda[it.prendaId] || {};
        const precioActual = Number(info.precioUnitario || 0);
        const subItem = cant * precioActual;
        p.desglosePorTipo[tipo].cantidad += cant;
        p.desglosePorTipo[tipo].subtotal += subItem;
        subtotalesPorTipo[tipo].cantidad += cant;
        subtotalesPorTipo[tipo].subtotal += subItem;
      });
    });

    const arr = Array.from(prendasMap.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    );
    const kardexConPrecio = arr.map((p) => {
      const info = preciosPorPrenda[p.prendaId] || {};
      const precio = Number(info.precioUnitario || 0);
      return {
        ...p,
        precioUnitario: precio,
        subtotal: precio * p.cantidad,
      };
    });

    return {
      kardex: kardexConPrecio,
      kardexCantidades: cantMap,
      entregasParaKardex: entregasOrd,
      subtotalesPorTipo,
    };
  }, [entregasSeleccionadas, preciosPorPrenda]);

  const totalKardex = useMemo(
    () => kardex.reduce((s, p) => s + (p.subtotal || 0), 0),
    [kardex],
  );

  const totalCantidadKardex = useMemo(
    () => kardex.reduce((s, p) => s + p.cantidad, 0),
    [kardex],
  );

  // Cambia precio de una prenda
  const handlePrecioChange = (prendaId, value) => {
    const num = Number(value);
    if (isNaN(num) || num < 0) return;
    setPreciosPorPrenda((prev) => ({
      ...prev,
      [prendaId]: {
        ...(prev[prendaId] || {}),
        nombre: prev[prendaId]?.nombre || kardex.find((k) => k.prendaId === prendaId)?.nombre || '',
        precioUnitario: num,
      },
    }));
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAllDisponibles = () => {
    const ids = entregas.filter((e) => !e.liquidada).map((e) => e._id);
    setSelectedIds(new Set(ids));
  };

  const handleSelectNone = () => setSelectedIds(new Set());

  const handleCrear = async () => {
    if (selectedIds.size === 0) {
      toast({ variant: 'destructive', title: 'Sin entregas seleccionadas' });
      return;
    }
    setCreando(true);
    try {
      // Tomamos solo los precios de las prendas que están en el kardex
      const preciosCustom = {};
      kardex.forEach((p) => {
        preciosCustom[p.prendaId] = p.precioUnitario;
      });
      const rango = `${dateStart} a ${dateEnd}`;
      const nombre = `Liquidación ${clienteNombre}${rango ? ' - ' + rango : ''}`.trim();

      const res = await crearLiquidacion({
        cliente: {
          id: clienteId,
          nombre: clienteNombre,
          nit: clienteNit || '',
        },
        nombre,
        fechaDesde: dateStart,
        fechaHasta: dateEnd,
        creado_por: user
          ? { id: user.id, nombre: user.name || user.raw?.nombre || 'Usuario' }
          : undefined,
        entregasIds: Array.from(selectedIds),
        preciosCustom,
      });
      toast({
        title: 'Liquidación creada',
        description: `${res.data.consecutivo} en estado Borrador.`,
      });
      onLiquidacionCreada?.(res.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setCreando(false);
    }
  };

  // Filtro de búsqueda
  const sorted = useMemo(
    () => [...entregas].sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion)),
    [entregas],
  );
  const filtered = useMemo(() => {
    if (!search.trim()) return sorted;
    const term = search.toLowerCase();
    return sorted.filter((e) => {
      return (
        (e.consecutivo || '').toLowerCase().includes(term) ||
        (e.lavanderia?.nombre || '').toLowerCase().includes(term) ||
        (e.conductor?.nombre || '').toLowerCase().includes(term)
      );
    });
  }, [sorted, search]);

  const selectedCount = selectedIds.size;
  const disponiblesCount = entregas.filter((e) => !e.liquidada).length;
  const liquidadasCount = entregas.filter((e) => e.liquidada).length;

  return (
    <div className="space-y-4">
      {/* Filtros de rango */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Fecha Inicio</label>
          <input
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Fecha Fin</label>
          <input
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <Button
          onClick={cargar}
          disabled={loading}
          className="bg-gray-900 hover:bg-gray-700 h-9 text-sm"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Cargar entregas
        </Button>
      </div>

      {/* KARDEX SUPERIOR — prendas de las entregas seleccionadas */}
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border-2 border-emerald-300 bg-emerald-50/30 p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                Kardex de prendas a liquidar
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Vista agregada por prenda de las {selectedCount} entregas seleccionadas.
                Ajustá los precios si es necesario.
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSelectNone}
              className="h-7 text-xs"
            >
              <X className="h-3 w-3 mr-1" /> Limpiar selección
            </Button>
          </div>
          {kardex.length === 0 ? (
            <p className="text-sm text-gray-500 py-3">
              Las entregas seleccionadas no tienen prendas confirmadas.
            </p>
          ) : (
            <div className="space-y-3">
              {/* Tabla 1: Kardex transaccional (prendas × entregas) */}
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Kardex detallado por transacción
                </p>
                <div className="overflow-x-auto rounded-md border bg-white">
                  <table className="text-xs border-collapse min-w-max">
                    <thead>
                      <tr>
                        <th className="text-left px-3 py-2 border border-gray-200 bg-gray-50 min-w-[140px] sticky left-0 z-10">
                          Prenda
                        </th>
                        {entregasParaKardex.map((e) => {
                          const tipo = e.tipoEntrega || 'ordinaria';
                          const colCls =
                            tipo === 'dominical'
                              ? 'bg-amber-50/60'
                              : tipo === 'extra'
                                ? 'bg-violet-50/60'
                                : 'bg-gray-50';
                          const badge =
                            tipo === 'dominical' ? (
                              <span className="inline-block text-[9px] font-bold uppercase tracking-wide bg-amber-200 text-amber-800 rounded px-1.5 py-0.5 mb-0.5">
                                Dom
                              </span>
                            ) : tipo === 'extra' ? (
                              <span className="inline-block text-[9px] font-bold uppercase tracking-wide bg-violet-200 text-violet-800 rounded px-1.5 py-0.5 mb-0.5">
                                Extra
                              </span>
                            ) : (
                              <span className="inline-block text-[9px] font-bold uppercase tracking-wide bg-gray-200 text-gray-600 rounded px-1.5 py-0.5 mb-0.5">
                                Ord
                              </span>
                            );
                          return (
                            <th
                              key={e._id}
                              className={`px-2 py-1.5 border border-gray-200 text-center min-w-[88px] ${colCls}`}
                              title={`${e.lavanderia?.nombre || ''} · ${e.consecutivo} (${tipo})`}
                            >
                              {badge}
                              <div className="font-semibold text-[11px] text-gray-700 leading-tight">
                                {e.consecutivo}
                              </div>
                              <div className="text-gray-400 text-[10px]">
                                {formatDate(e.fecha_entrega || e.fecha_creacion)}
                              </div>
                            </th>
                          );
                        })}
                        <th className="px-3 py-2 border border-gray-200 bg-gray-50 text-center font-bold min-w-[80px]">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {kardex.map((p) => (
                        <tr key={p.prendaId} className="hover:bg-gray-50">
                          <td className="px-3 py-2 border border-gray-200 font-medium text-gray-800 sticky left-0 bg-white">
                            {p.nombre}
                          </td>
                          {entregasParaKardex.map((e) => {
                            const cant = kardexCantidades[p.prendaId]?.[e._id] || 0;
                            const tipo = e.tipoEntrega || 'ordinaria';
                            const cellCls =
                              tipo === 'dominical'
                                ? 'bg-amber-50/60'
                                : tipo === 'extra'
                                  ? 'bg-violet-50/60'
                                  : '';
                            if (cant === 0) {
                              return (
                                <td
                                  key={e._id}
                                  className={`px-2 py-1.5 border border-gray-100 text-center text-gray-300 ${cellCls}`}
                                >
                                  —
                                </td>
                              );
                            }
                            return (
                              <td
                                key={e._id}
                                className={`px-2 py-1.5 border border-gray-100 text-center ${cellCls || 'bg-emerald-50'}`}
                              >
                                <div className="font-bold text-emerald-700 text-xs">
                                  +{cant}
                                </div>
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 border border-gray-200 text-center font-bold bg-emerald-50 text-emerald-700">
                            {p.cantidad}
                          </td>
                        </tr>
                      ))}
                      {/* Fila de totales por entrega */}
                      <tr className="border-t-2 border-gray-300 bg-gray-100">
                        <td className="px-3 py-2 border border-gray-200 font-bold text-gray-700 text-[11px] uppercase sticky left-0 bg-gray-100">
                          Total
                        </td>
                        {entregasParaKardex.map((e) => {
                          const totalEntrega = kardex.reduce(
                            (s, p) => s + (kardexCantidades[p.prendaId]?.[e._id] || 0),
                            0,
                          );
                          const tipo = e.tipoEntrega || 'ordinaria';
                          const cellCls =
                            tipo === 'dominical'
                              ? 'bg-amber-100/70'
                              : tipo === 'extra'
                                ? 'bg-violet-100/70'
                                : '';
                          return (
                            <td
                              key={e._id}
                              className={`px-2 py-2 border border-gray-200 text-center font-bold text-emerald-700 text-xs ${cellCls}`}
                            >
                              {totalEntrega > 0 ? totalEntrega : '—'}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 border border-gray-200 text-center font-bold text-emerald-700 text-sm">
                          {totalCantidadKardex}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tabla 2: Precios y subtotales (bloques por tipo) */}
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Precios y subtotales
                </p>
                <div className="overflow-x-auto rounded-md border bg-white">
                  <table className="w-full text-sm">
                    {/* Sub-encabezado: nombre del tipo (Ordinarias, Extras, Dominicales) */}
                    <thead>
                      <tr className="text-xs">
                        <th
                          rowSpan={2}
                          className="text-left px-3 py-2 font-medium border-b border-r border-gray-200 bg-gray-50 align-bottom min-w-[140px]"
                        >
                          Prenda
                        </th>
                        <th
                          colSpan={3}
                          className="text-center px-2 py-1.5 font-semibold border-b border-l border-gray-200 bg-gray-100 text-gray-700"
                        >
                          <span className="inline-block text-[9px] font-bold bg-gray-200 text-gray-700 rounded px-1.5 py-0.5 mr-1">Ord</span>
                          Ordinarias
                        </th>
                        <th
                          colSpan={3}
                          className="text-center px-2 py-1.5 font-semibold border-b border-l border-gray-200 bg-violet-100 text-violet-800"
                        >
                          <span className="inline-block text-[9px] font-bold bg-violet-200 text-violet-800 rounded px-1.5 py-0.5 mr-1">Extra</span>
                          Extras
                        </th>
                        <th
                          colSpan={3}
                          className="text-center px-2 py-1.5 font-semibold border-b border-l border-gray-200 bg-amber-100 text-amber-800"
                        >
                          <span className="inline-block text-[9px] font-bold bg-amber-200 text-amber-800 rounded px-1.5 py-0.5 mr-1">Dom</span>
                          Dominicales
                        </th>
                        <th
                          rowSpan={2}
                          className="text-right px-3 py-2 font-medium border-b border-l border-gray-200 bg-emerald-50 align-bottom min-w-[100px]"
                        >
                          Total
                        </th>
                      </tr>
                      {/* Sub-encabezado: Cant / Precio / Subtotal por cada tipo */}
                      <tr className="text-[10px] text-gray-500 uppercase tracking-wide">
                        {(['ordinaria', 'extra', 'dominical']).flatMap((tipo) => [
                          <th key={`${tipo}-cant`} className={`text-right px-1 py-1 font-medium border-b border-l border-gray-200 ${tipo === 'extra' ? 'bg-violet-50' : tipo === 'dominical' ? 'bg-amber-50' : 'bg-white'}`}>Cant</th>,
                          <th key={`${tipo}-pre`} className={`text-right px-1 py-1 font-medium border-b border-l border-gray-200 ${tipo === 'extra' ? 'bg-violet-50' : tipo === 'dominical' ? 'bg-amber-50' : 'bg-white'}`}>Precio</th>,
                          <th key={`${tipo}-sub`} className={`text-right px-1 py-1 font-medium border-b border-l border-gray-200 ${tipo === 'extra' ? 'bg-violet-50' : tipo === 'dominical' ? 'bg-amber-50' : 'bg-white'}`}>Subtotal</th>,
                        ])}
                      </tr>
                    </thead>
                    <tbody>
                      {kardex.map((p) => {
                        // Para cada tipo: cant, precio (del primer item de ese tipo), subtotal
                        const cellByTipo = {};
                        (['ordinaria', 'extra', 'dominical']).forEach((tipo) => {
                          const cant = p.desglosePorTipo?.[tipo]?.cantidad || 0;
                          const sub = p.desglosePorTipo?.[tipo]?.subtotal || 0;
                          // Precio = subtotal / cantidad (si hay cantidad)
                          const precio = cant > 0 ? sub / cant : 0;
                          cellByTipo[tipo] = { cant, sub, precio };
                        });

                        return (
                          <tr key={p.prendaId} className="border-t border-gray-200">
                            <td className="py-2 px-3 font-medium align-top">{p.nombre}</td>
                            {(['ordinaria', 'extra', 'dominical']).flatMap((tipo) => {
                              const { cant, sub, precio } = cellByTipo[tipo];
                              const tiene = cant > 0;
                              const colorCls =
                                tipo === 'dominical' ? 'text-amber-700' : tipo === 'extra' ? 'text-violet-700' : 'text-gray-700';
                              const cellBg =
                                tipo === 'extra' ? 'bg-violet-50/30' : tipo === 'dominical' ? 'bg-amber-50/30' : '';
                              return [
                                <td key={`${p.prendaId}-${tipo}-cant`} className={`py-2 px-1 text-right tabular-nums align-top border-l border-gray-100 ${cellBg}`}>
                                  {tiene ? <span className={`font-semibold ${colorCls}`}>{cant}</span> : <span className="text-gray-300">—</span>}
                                </td>,
                                <td key={`${p.prendaId}-${tipo}-pre`} className={`py-2 px-1 text-right tabular-nums align-top ${cellBg}`}>
                                  {tiene ? <span className="text-gray-700">{formatCurrency(precio)}</span> : <span className="text-gray-300">—</span>}
                                </td>,
                                <td key={`${p.prendaId}-${tipo}-sub`} className={`py-2 px-1 text-right tabular-nums align-top ${cellBg}`}>
                                  {tiene ? <span className={`font-semibold ${colorCls}`}>{formatCurrency(sub)}</span> : <span className="text-gray-300">—</span>}
                                </td>,
                              ];
                            })}
                            <td className="py-2 px-3 text-right tabular-nums font-bold text-emerald-700 align-top border-l border-gray-200 bg-emerald-50">
                              {formatCurrency(p.subtotal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300 text-xs">
                      {/* Gran total por tipo (resumen de cada bloque) */}
                      <tr>
                        <td className="py-1.5 px-3 font-semibold text-gray-700">Subtotales</td>
                        {(['ordinaria', 'extra', 'dominical']).flatMap((tipo) => {
                          const data = subtotalesPorTipo[tipo];
                          const colorCls =
                            tipo === 'dominical' ? 'text-amber-700' : tipo === 'extra' ? 'text-violet-700' : 'text-gray-700';
                          const cellBg =
                            tipo === 'extra' ? 'bg-violet-50/30' : tipo === 'dominical' ? 'bg-amber-50/30' : '';
                          return [
                            <td key={`ft-${tipo}-cant`} className={`py-1.5 px-1 text-right tabular-nums font-semibold border-l border-gray-100 ${cellBg}`}>
                              {data.cantidad > 0 ? <span className={colorCls}>{data.cantidad}</span> : <span className="text-gray-300">—</span>}
                            </td>,
                            <td key={`ft-${tipo}-pre`} className={`py-1.5 px-1 text-right tabular-nums text-gray-400 ${cellBg}`}>
                              —
                            </td>,
                            <td key={`ft-${tipo}-sub`} className={`py-1.5 px-1 text-right tabular-nums font-semibold ${cellBg}`}>
                              {data.subtotal > 0 ? <span className={colorCls}>{formatCurrency(data.subtotal)}</span> : <span className="text-gray-300">—</span>}
                            </td>,
                          ];
                        })}
                        <td className="py-1.5 px-3 text-right tabular-nums font-bold text-emerald-700 border-l border-gray-200 bg-emerald-50">
                          {formatCurrency(totalKardex)}
                        </td>
                      </tr>
                      {/* Gran total */}
                      <tr className="border-t border-emerald-200 bg-emerald-50/60">
                        <td className="py-2 px-3 font-bold text-gray-800 text-sm">TOTAL</td>
                        {(['ordinaria', 'extra', 'dominical']).flatMap((tipo) => {
                          const cant = subtotalesPorTipo[tipo].cantidad;
                          const colorCls =
                            tipo === 'dominical' ? 'text-amber-700' : tipo === 'extra' ? 'text-violet-700' : 'text-gray-700';
                          return [
                            <td key={`gt-${tipo}-cant`} className="py-2 px-1 text-right tabular-nums font-bold border-l border-emerald-200">
                              {cant > 0 ? <span className={colorCls}>{cant}</span> : <span className="text-gray-300">—</span>}
                            </td>,
                            <td key={`gt-${tipo}-pre`} className="py-2 px-1 text-right tabular-nums text-gray-400">
                              —
                            </td>,
                            <td key={`gt-${tipo}-sub`} className="py-2 px-1 text-right tabular-nums font-bold">
                              {subtotalesPorTipo[tipo].subtotal > 0 ? (
                                <span className={colorCls}>{formatCurrency(subtotalesPorTipo[tipo].subtotal)}</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>,
                          ];
                        })}
                        <td className="py-2 px-3 text-right tabular-nums font-bold text-lg text-emerald-700 border-l border-emerald-200">
                          {formatCurrency(totalKardex)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-emerald-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Disponibles</p>
          <p className="text-xl font-bold text-emerald-700">{disponiblesCount}</p>
        </div>
        <div className="bg-violet-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Liquidadas</p>
          <p className="text-xl font-bold text-violet-700">{liquidadasCount}</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Seleccionadas</p>
          <p className="text-xl font-bold text-amber-700">{selectedCount}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-3 text-white">
          <p className="text-xs text-gray-300 flex items-center gap-1">
            <DollarSign className="h-3 w-3" /> Total a liquidar
          </p>
          <p className="text-xl font-bold">{formatCurrency(totalKardex)}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por consecutivo, lavandería o conductor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9"
          />
        </div>
        <span className="text-xs text-gray-500">
          {selectedCount} de {disponiblesCount} seleccionadas
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={handleSelectAllDisponibles}
        >
          <ListChecks className="h-3.5 w-3.5 mr-1" /> Todas
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={handleSelectNone}
          disabled={selectedCount === 0}
        >
          <X className="h-3.5 w-3.5 mr-1" /> Ninguna
        </Button>
      </div>

      {/* Lista de entregas */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed">
          <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Sin entregas en el período seleccionado.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((e) => (
            <EntregaRow
              key={e._id}
              entrega={e}
              selected={selectedIds.has(e._id)}
              onToggle={() => handleToggleSelect(e._id)}
            />
          ))}
        </div>
      )}

      {/* CTA Crear liquidación */}
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky bottom-0 -mx-4 md:-mx-4 px-4 py-3 bg-white border-t mt-4"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm text-gray-600">
                Vas a crear una liquidación con{' '}
                <span className="font-bold text-gray-900">{selectedCount}</span> entregas
                por un total de{' '}
                <span className="font-bold text-emerald-700">
                  {formatCurrency(totalKardex)}
                </span>
                .
              </p>
            </div>
            <Button
              onClick={handleCrear}
              disabled={creando || selectedCount === 0 || kardex.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creando ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Crear liquidación en Borrador
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Fila de entrega con checkbox de selección
// ─────────────────────────────────────────────────────────────────────
const EntregaRow = ({ entrega, selected, onToggle }) => {
  const totalCantidad = (entrega.items || []).reduce(
    (s, i) => s + (i.cantidad_confirmada || 0), 0,
  );
  const isLiquidada = entrega.liquidada;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: isLiquidada ? 0.55 : selected ? 1 : 0.85, y: 0 }}
      transition={{ duration: 0.15 }}
      className="flex items-stretch gap-2"
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={isLiquidada}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        title={
          isLiquidada
            ? 'Esta entrega ya fue liquidada'
            : selected
              ? 'Quitar de la selección'
              : 'Incluir en la liquidación'
        }
        className="h-4 w-4 cursor-pointer accent-emerald-600 mt-3 shrink-0 disabled:cursor-not-allowed"
      />
      <div
        className={`flex-1 border-l-4 rounded-md bg-white border border-border/60 p-3 ${
          selected ? 'border-l-emerald-500' : 'border-l-gray-300'
        }`}
      >
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-mono text-sm font-semibold text-emerald-700">
            {entrega.consecutivo}
          </span>
          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Entrega</Badge>
          {isLiquidada && (
            <Badge className="bg-violet-100 text-violet-700 text-xs gap-0.5">
              <Receipt className="h-3 w-3" /> Liquidada
              {entrega.liquidacionConsecutivo ? ` · ${entrega.liquidacionConsecutivo}` : ''}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {formatDate(entrega.fecha_entrega || entrega.fecha_creacion)}
          </span>
          <span className="flex items-center gap-1">
            <Package className="h-3 w-3" /> {totalCantidad} prendas
          </span>
          {entrega.lavanderia?.nombre && (
            <span className="flex items-center gap-1">
              <Building2 className="h-3 w-3" /> {entrega.lavanderia.nombre}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Modal: detalle de una liquidación existente (ver/editar/confirmar/anular)
// ─────────────────────────────────────────────────────────────────────
const LiquidacionDetailModal = ({ open, onClose, liquidacionId, onChanged }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [editingNombre, setEditingNombre] = useState(false);
  const [nombreTemp, setNombreTemp] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [anularOpen, setAnularOpen] = useState(false);
  const [motivo, setMotivo] = useState('');

  const load = useCallback(async () => {
    if (!liquidacionId) return;
    setLoading(true);
    try {
      const res = await getLiquidacionById(liquidacionId);
      setData(res.data);
      setNombreTemp(res.data?.nombre || '');
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [liquidacionId, toast]);

  useEffect(() => {
    if (open) {
      setEditingNombre(false);
      setAnularOpen(false);
      setMotivo('');
      load();
    }
  }, [open, load]);

  const editable = data?.estado === 'Borrador';

  const handlePrecioChange = async (entregaId, prendaId, value) => {
    if (!data) return;
    const num = Number(value);
    if (isNaN(num) || num < 0) return;
    const key = `${entregaId}::${prendaId}`;
    setSavingKey(key);
    try {
      const next = JSON.parse(JSON.stringify(data));
      let total = 0;
      for (const e of next.entregas) {
        let sub = 0;
        for (const it of e.items) {
          if (e.entregaId === entregaId && it.prendaId === prendaId) {
            it.precioUnitario = num;
            it.subtotal = (it.cantidad || 0) * num;
          }
          sub += Number(it.subtotal || 0);
        }
        e.subtotalEntrega = sub;
        total += sub;
      }
      next.total = total;
      setData(next);
      const res = await updatePrecioItem(data._id, {
        entregaId, prendaId, precioUnitario: num,
      });
      setData(res.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setSavingKey(null);
    }
  };

  const handleSaveNombre = async () => {
    if (!data) return;
    try {
      const res = await updateMetadataLiquidacion(data._id, { nombre: nombreTemp });
      setData(res.data);
      setEditingNombre(false);
      toast({ title: 'Nombre actualizado' });
      onChanged?.(res.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    }
  };

  const handleConfirmar = async () => {
    if (!data || !user) return;
    setConfirming(true);
    try {
      const res = await confirmarLiquidacion(data._id, {
        id: user.id, nombre: user.name || user.raw?.nombre || 'Usuario',
      });
      setData(res.data);
      toast({
        title: 'Liquidación confirmada',
        description: 'Se descargó el Excel con el detalle.',
      });
      onChanged?.(res.data);
      // Descarga el Excel automáticamente al confirmar
      setTimeout(() => {
        try {
          exportarLiquidacionExcel({ liquidacion: res.data });
        } catch (e) {
          console.error('No se pudo descargar el Excel:', e);
        }
      }, 200);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    } finally {
      setConfirming(false);
    }
  };

  const handleAnular = async () => {
    if (!data || !user) return;
    if (!motivo.trim()) {
      toast({ variant: 'destructive', title: 'Indica el motivo' });
      return;
    }
    try {
      const res = await anularLiquidacion(
        data._id,
        motivo.trim(),
        { id: user.id, nombre: user.name || user.raw?.nombre || 'Usuario' },
      );
      setData(res.data);
      setAnularOpen(false);
      toast({ title: 'Liquidación anulada' });
      onChanged?.(res.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.message });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[94vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-600" />
              {data ? `Liquidación ${data.consecutivo}` : 'Cargando…'}
            </DialogTitle>
            {data && (
              <DialogDescription>
                {data.cliente?.nombre} {data.cliente?.nit ? `· NIT ${data.cliente.nit}` : ''}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading || !data ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-md bg-gray-50">
                    <p className="text-xs text-gray-500">Estado</p>
                    <Badge className={`mt-1 ${estadoColor[data.estado] || ''}`}>{data.estado}</Badge>
                  </div>
                  <div className="p-3 rounded-md bg-gray-50">
                    <p className="text-xs text-gray-500">Creada</p>
                    <p className="text-sm">{formatDateTime(data.fecha_creacion)}</p>
                  </div>
                  <div className="p-3 rounded-md bg-gray-50">
                    <p className="text-xs text-gray-500">Liquidada</p>
                    <p className="text-sm">
                      {data.fecha_liquidacion ? formatDateTime(data.fecha_liquidacion) : '—'}
                    </p>
                  </div>
                  <div className="p-3 rounded-md bg-emerald-500/10">
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-lg font-bold text-emerald-700">
                      {formatCurrency(data.total)}
                    </p>
                  </div>
                </div>

                <div className="p-3 rounded-md border space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-500">Nombre</p>
                    {editable && !editingNombre && (
                      <Button size="sm" variant="ghost" onClick={() => setEditingNombre(true)}>
                        Editar
                      </Button>
                    )}
                  </div>
                  {editable && editingNombre ? (
                    <div className="flex gap-2">
                      <Input value={nombreTemp} onChange={(e) => setNombreTemp(e.target.value)} />
                      <Button size="sm" onClick={handleSaveNombre}>Guardar</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingNombre(false);
                          setNombreTemp(data.nombre);
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <p className="font-medium">{data.nombre}</p>
                  )}
                </div>

                <LiquidacionDetailList
                  entregas={data.entregas}
                  editable={editable}
                  onPrecioChange={handlePrecioChange}
                  savingKey={savingKey}
                />
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
            {data && (
              <Button
                variant="outline"
                onClick={() => exportarLiquidacionExcel({ liquidacion: data })}
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Descargar Excel
              </Button>
            )}
            {data?.estado === 'Borrador' && (
              <Button
                onClick={handleConfirmar}
                disabled={confirming}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Confirmar liquidación
              </Button>
            )}
            {data?.estado === 'Liquidada' && (
              <Button variant="destructive" onClick={() => setAnularOpen(true)}>
                <Ban className="h-4 w-4 mr-2" /> Anular
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={anularOpen} onOpenChange={setAnularOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Anular esta liquidación?</AlertDialogTitle>
            <AlertDialogDescription>
              Se liberarán las entregas para que puedan incluirse en otra liquidación.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs text-gray-500">Motivo *</label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. Error en cantidades / precios mal aplicados"
            />
          </div>
          <AlertFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAnular}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Anular y liberar
            </AlertDialogAction>
          </AlertFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LiquidacionClientePage;
